/**
 * Floating offline indicator component
 * Shows a banner at the top when the app is offline
 */

import { html, css, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { networkState } from 'src/utilities/network-error-handler';

@customElement('d-offline-indicator')
export class DOfflineIndicator extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      position: fixed;
      bottom: 20px;
      left: 20px;
      z-index: 9999;
      pointer-events: none;
    }

    .offline-indicator {
      display: flex;
      align-items: center;
      gap: 8px;
      background: rgba(0, 0, 0, 0.6);
      backdrop-filter: blur(8px);
      color: white;
      padding: 10px 14px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 500;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
      animation: fadeIn 0.3s ease-out;
      pointer-events: auto;
    }

    .offline-indicator-hidden {
      display: none;
    }

    .icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      font-size: 18px;
      flex-shrink: 0;
    }

    .text {
      display: none;
    }

    @media (min-width: 768px) {
      .text {
        display: block;
      }
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;

  // Delay before showing offline banner to avoid false positives during brief interruptions
  private static readonly OFFLINE_DELAY_MS = 10_000;

  @state()
  private isOffline = !navigator.onLine;

  private _offlineTimer: ReturnType<typeof setTimeout> | null = null;

  connectedCallback() {
    super.connectedCallback();

    // Listen to network events
    window.addEventListener('network-offline', this.handleOffline);
    window.addEventListener('network-online', this.handleOnline);

    // Also listen to native online/offline events as backup
    window.addEventListener('offline', this.handleOffline);
    window.addEventListener('online', this.handleOnline);

    // Set initial state
    this.isOffline = !networkState.isOnline;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up event listeners
    window.removeEventListener('network-offline', this.handleOffline);
    window.removeEventListener('network-online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    window.removeEventListener('online', this.handleOnline);

    if (this._offlineTimer !== null) {
      clearTimeout(this._offlineTimer);
      this._offlineTimer = null;
    }
  }

  private handleOffline = () => {
    console.log('[OfflineIndicator] Connection lost, will show banner after delay');
    if (this._offlineTimer === null) {
      this._offlineTimer = setTimeout(() => {
        this._offlineTimer = null;
        this.isOffline = true;
        console.log('[OfflineIndicator] Showing offline banner');
      }, DOfflineIndicator.OFFLINE_DELAY_MS);
    }
  };

  private handleOnline = () => {
    console.log('[OfflineIndicator] Going online');
    if (this._offlineTimer !== null) {
      clearTimeout(this._offlineTimer);
      this._offlineTimer = null;
    }
    this.isOffline = false;
  };

  render() {
    return html`
      <div class="${this.isOffline ? 'offline-indicator' : 'offline-indicator-hidden'}">
        <span class="icon">📡</span>
        <span class="text">Frakoblet</span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-offline-indicator': DOfflineIndicator;
  }
}
